import { useEffect, useMemo, useState } from "react";
import { isHalloween } from "@/common/utils/halloween";

const MESSAGE_CYCLE_INTERVAL = 7000;

const SHUFFLEABLE_MESSAGES = [
    "Herding cats...",
    "Spinning up hamsters...",
    "Aligning ducks...",
    "Fetching retrievers...",
    "Queueing quarter horses...",
    "Validating gerbils...",
    "Indexing iguanas...",
    "Testing canaries...",
    "Querying cockatiels...",
    "Routing rabbits...",
    "Caching chinchillas...",
    "Thanks for being patient...",
];

// EN-1476: swapped in while isHalloween(), for every caller (document loader, document builder
// loader, Scout's in-chat "Working..." text). Only the shuffled pool changes, so the status line a
// caller passes first ("Writing document...", "Transcribing audio...") is unchanged.
const HALLOWEEN_MESSAGES = [
    "Herding black cats...",
    "Waking the bats...",
    "Carving pumpkins...",
    "Stirring the cauldron...",
    "Untangling cobwebs...",
    "Summoning the scribes...",
    "Sorting the candy corn...",
    "Fetching werewolves...",
    "Thanks for being patient...",
];

/**
 * Shuffles an array in place using the Fisher-Yates algorithm.
 * @param array - the array to shuffle
 * @returns the shuffled array
 */
const shuffleArray = <T>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

interface UseThinkingMessageOptions {
    /** Initial messages to show before the shuffled messages */
    initialMessages?: string[];
    /** Interval in milliseconds between message changes. Defaults to 7000ms. */
    interval?: number;
    /** Whether to enable cycling through messages. Defaults to true. */
    enabled?: boolean;
}

/**
 * Hook that cycles through "thinking" messages to keep loading states interesting.
 * The messages start with any initial messages in order, followed by shuffled fun messages.
 * @param options - configuration options
 * @returns the current message and message index (useful for animation keys)
 */
export const useThinkingMessage = (options: UseThinkingMessageOptions = {}) => {
    const { initialMessages = [], interval = MESSAGE_CYCLE_INTERVAL, enabled = true } = options;

    const thinkingMessages = useMemo(
        () => [...initialMessages, ...shuffleArray(isHalloween() ? HALLOWEEN_MESSAGES : SHUFFLEABLE_MESSAGES)],
        [initialMessages],
    );

    const [messageIndex, setMessageIndex] = useState(0);

    useEffect(() => {
        // Reset to the first message when thinkingMessages changes
        setMessageIndex(0);

        if (!enabled) {
            return;
        }

        const intervalId = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % thinkingMessages.length);
        }, interval);

        return () => {
            clearInterval(intervalId);
        };
    }, [thinkingMessages, interval, enabled]);

    return {
        message: thinkingMessages[messageIndex],
        messageIndex,
    };
};
